import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import api from "@/lib/api";

const LanguageContext = createContext(null);
const STORAGE_LANG = "myazbnb_lang";
const cacheKey = (lang) => `myazbnb_tr_${lang}`;

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => localStorage.getItem(STORAGE_LANG) || "en");
  const [map, setMap] = useState(() => {
    try { return JSON.parse(localStorage.getItem(cacheKey(localStorage.getItem(STORAGE_LANG) || "en")) || "{}"); }
    catch { return {}; }
  });
  const pending = useRef(new Set());
  const timer = useRef(null);
  const [, force] = useState(0);

  const setLang = useCallback((next) => {
    localStorage.setItem(STORAGE_LANG, next);
    setLangState(next);
  }, []);

  useEffect(() => {
    if (lang === "en") { setMap({}); return; }
    try { setMap(JSON.parse(localStorage.getItem(cacheKey(lang)) || "{}")); }
    catch { setMap({}); }
  }, [lang]);

  const flush = useCallback(async () => {
    const texts = [...pending.current];
    pending.current.clear();
    if (!texts.length || lang === "en") return;
    try {
      const { data } = await api.post("/translate", { texts, target: lang });
      setMap((prev) => {
        const next = { ...prev, ...data.translations };
        try { localStorage.setItem(cacheKey(lang), JSON.stringify(next)); } catch { /* quota */ }
        return next;
      });
    } catch { /* keep english fallback */ }
  }, [lang]);

  const t = useCallback((text) => {
    if (!text || lang === "en" || typeof text !== "string") return text;
    if (map[text] !== undefined) return map[text];
    if (!pending.current.has(text)) {
      pending.current.add(text);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => flush().then(() => force((n) => n + 1)), 200);
    }
    return text;
  }, [lang, map, flush]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) return { lang: "en", setLang: () => {}, t: (x) => x };
  return ctx;
}
